import React, { useEffect, useState } from "react";
import "./App.css";
import "./body.css";
import "antd/dist/antd.css";
import { Layout } from "antd";
import cubejs from "@cubejs-client/core";
import { CubeProvider } from "@cubejs-client/react";
import Header from "./components/Header";
import { getToken, getUser, isAuthenticated, isKeycloakAuth, checkKeycloakSession, logout, getKeycloakConfig, syncKcIdTokenToSession } from "./auth";

const API_URL = window.location.origin;

export const AuthContext = React.createContext(null);

const AppLayout = ({ children }) => (
  <Layout style={{ height: "100%" }}>
    <Header />
    <Layout.Content>{children}</Layout.Content>
  </Layout>
);

const App = ({ children }) => {
  const [sessionChecked, setSessionChecked] = useState(!isKeycloakAuth());

  useEffect(() => {
    // Mirror kc_id_token from localStorage into this tab's sessionStorage so
    // that on cross-tab logout (when localStorage gets cleared by the
    // originating tab) we can still send id_token_hint to KC. Without that
    // hint KC shows its "You are logged out" intermediate page.
    syncKcIdTokenToSession();

    // On page load, if user logged in via Keycloak, verify KC session is still active.
    // If the user logged out from Etendo (which does KC logout), this will detect it
    // and force a local logout so TankView/MWO don't stay active after SSO logout.
    if (isAuthenticated() && isKeycloakAuth()) {
      checkKeycloakSession().then((active) => {
        if (!active) {
          logout();
        } else {
          setSessionChecked(true);
        }
      });
    }

    // Re-check the KC session whenever the tab regains focus or becomes
    // visible. Without this, a TankView tab left open in the background
    // would stay logged in forever after the user logged out from Etendo
    // or another KC-aware app, since the only previous check ran on mount.
    // Throttled to one check per minute to avoid hammering KC on rapid
    // tab-switches.
    let lastSessionCheck = Date.now();
    const recheckSession = () => {
      if (!isAuthenticated() || !isKeycloakAuth()) return;
      const now = Date.now();
      if (now - lastSessionCheck < 60000) return;
      lastSessionCheck = now;
      checkKeycloakSession().then((active) => {
        if (!active) logout();
      });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") recheckSession();
    };
    window.addEventListener("focus", recheckSession);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Cross-tab logout: TankView is treated as one app, so when any mini-app
    // (Dashboard, WOW, etc.) logs out, all other open TV tabs are routed to
    // the same Etendo goodbye landing page for a consistent UX. Going
    // straight to #/login here previously caused the KC session (still alive
    // until the user clicked "here") to auto-re-authenticate the other tab
    // and land it on the default /needsaction route.
    const onStorageChange = async (e) => {
      if (e.key !== "tv_auth_token" || e.newValue) return;
      const config = await getKeycloakConfig().catch(() => null);
      if (config && config.etendoLogoutUrl) {
        // sessionStorage is per-tab and survives the originating tab's
        // clearLocalSession() so we can still hand KC an id_token_hint.
        const idToken = sessionStorage.getItem("kc_id_token");
        const params = new URLSearchParams({
          logout: "true",
          client_id: config.clientId,
          post_logout_redirect_uri: window.location.origin + window.location.pathname,
        });
        if (idToken) params.set("id_token", idToken);
        sessionStorage.removeItem("kc_id_token");
        window.location.href = `${config.etendoLogoutUrl}?${params.toString()}`;
        return;
      }
      window.location.hash = "#/login";
      window.location.reload();
    };
    window.addEventListener("storage", onStorageChange);
    return () => {
      window.removeEventListener("storage", onStorageChange);
      window.removeEventListener("focus", recheckSession);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  if (!isAuthenticated()) {
    // No auth - render children directly (LoginPage doesn't need CubeProvider)
    return <div>{children}</div>;
  }

  // Wait for KC session check before rendering authenticated content
  if (!sessionChecked) {
    return null;
  }

  const token = getToken();
  const user = getUser();
  const cubejsApi = cubejs(token, { apiUrl: `${API_URL}/cubejs-api/v1` });

  return (
    <AuthContext.Provider value={user}>
      <CubeProvider cubejsApi={cubejsApi}>
        <AppLayout>{children}</AppLayout>
      </CubeProvider>
    </AuthContext.Provider>
  );
};

export default App;
